import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, frDate } from "@/lib/constants";
import { Wallet, Percent, TrendingUp, Receipt, AlertCircle, Landmark, Info, FileSpreadsheet, FileText, BarChart3, List } from "lucide-react";
import { Widget, PageHeader } from "@/components/Widget";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import StripeConnectProvider from "@/components/StripeConnectProvider";
import { ConnectBalances, ConnectPayouts } from "@stripe/react-connect-js";

const WHICH = { deposit: "Acompte", balance: "Solde", store: "Boutique" };

function Kpi({ icon: Icon, label, value, tint, i }) {
  return (
    <div data-testid={`compta-kpi-${label}`} className={`p-5 ${i ? "lg:border-l lg:border-zinc-800" : ""} ${i % 2 ? "sm:border-l sm:border-zinc-800" : ""} ${i >= 1 ? "border-t border-zinc-800 sm:border-t-0" : ""} ${i >= 2 ? "sm:border-t lg:border-t-0" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-zinc-500">{label}</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${tint}1a`, color: tint }}><Icon className="h-4.5 w-4.5" /></span>
      </div>
      <p className="mt-3 font-display text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function FiscalBlock({ f, periodLabel }) {
  const totalCharges = (f.urssaf_due || 0) + (f.tva_due || 0);
  const statusLabel = f.fiscal_status === "societe" ? "Société" : "Auto-entrepreneur";
  return (
    <Widget testid="compta-fiscal" icon={Landmark} title="Charges à prévoir" to="/app/parametres" toLabel="Régler les taux">
      <p className="mb-4 text-xs text-zinc-500">Estimation sur le CA encaissé · {statusLabel}{periodLabel ? ` · ${periodLabel}` : ""}</p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div data-testid="fiscal-ca-ht" className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
          <p className="text-xs text-zinc-500">Base HT</p>
          <p className="mt-1 font-display text-xl font-bold text-white">{eur(f.ca_ht)}</p>
          <p className="mt-0.5 text-[11px] text-zinc-600">{f.vat_registered ? `TTC ${eur(f.ca_ttc)}` : "Non assujetti à la TVA"}</p>
        </div>
        <div data-testid="fiscal-urssaf" className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
          <p className="text-xs text-zinc-500">URSSAF / cotisations</p>
          <p className="mt-1 font-display text-xl font-bold text-amber-400">{eur(f.urssaf_due)}</p>
          <p className="mt-0.5 text-[11px] text-zinc-600">{f.social_rate ? `${f.social_rate}% du CA HT` : "Taux non défini"}</p>
        </div>
        <div data-testid="fiscal-tva" className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
          <p className="text-xs text-zinc-500">TVA à déclarer</p>
          {f.vat_registered
            ? <><p className="mt-1 font-display text-xl font-bold text-sky-400">{eur(f.tva_due)}</p><p className="mt-0.5 text-[11px] text-zinc-600">{f.vat_rate}% collectée</p></>
            : <><p className="mt-1 font-display text-xl font-bold text-zinc-500">—</p><p className="mt-0.5 text-[11px] text-zinc-600">Franchise en base de TVA</p></>}
        </div>
        <div data-testid="fiscal-total" className="rounded-xl border border-[#FF6A00]/30 bg-[#FF6A00]/10 p-4">
          <p className="text-xs text-[#FF6A00]">Total charges estimées</p>
          <p className="mt-1 font-display text-xl font-bold text-white">{eur(totalCharges)}</p>
          <p className="mt-0.5 text-[11px] text-zinc-400">Reste net ≈ {eur((f.ca_ht || 0) - totalCharges)}</p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-zinc-950 p-3 text-xs text-zinc-500">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" />
        <span>Estimation indicative, non contractuelle. Les taux (URSSAF, TVA) et votre statut sont modifiables dans <Link to="/app/parametres" className="font-medium text-zinc-300 underline">Paramètres → Organisation</Link>. Vérifiez toujours auprès de l'URSSAF / des impôts.</span>
      </div>
    </Widget>
  );
}

export default function Comptabilite() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const [busy, setBusy] = useState("");
  const [connectPk, setConnectPk] = useState("");
  useEffect(() => { api.get(`/accounting/summary?period=${period}`).then(({ data }) => setData(data)).catch(() => {}); }, [period]);
  useEffect(() => { api.get("/connect/status").then(({ data }) => setConnectPk(data.publishable_key || "")).catch(() => {}); }, []);
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/accounting/export?period=${period}&format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `comptabilite-${period}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Échec de l'export"); } finally { setBusy(""); }
  };
  if (!data) return <p className="text-zinc-500">Chargement…</p>;
  const PERIODS = [["all", "Tout"], ["month", "Ce mois"], ["quarter", "Ce trimestre"], ["year", "Cette année"]];

  return (
    <div className="space-y-6" data-testid="comptabilite-page">
      <PageHeader title="Comptabilité" subtitle="Vos paiements encaissés et commissions plateforme, synchronisés depuis Stripe." actions={<>
          <div className="flex rounded-full bg-zinc-900 p-1" data-testid="compta-period">
            {PERIODS.map(([k, l]) => (
              <button key={k} data-testid={`compta-period-${k}`} onClick={() => setPeriod(k)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${period === k ? "bg-[#FF6A00] text-white" : "text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <button data-testid="compta-export-csv" onClick={() => download("csv")} disabled={busy === "csv"} className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-50"><FileSpreadsheet className="h-4 w-4" />CSV</button>
          <button data-testid="compta-export-pdf" onClick={() => download("pdf")} disabled={busy === "pdf"} className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-50"><FileText className="h-4 w-4" />PDF</button>
      </>} />

      {data.connect_status !== "active" && (
        <div data-testid="compta-connect-banner" className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-400">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>Activez l'encaissement Stripe pour recevoir vos paiements directement et suivre vos versements ici. <Link to="/app/parametres?tab=payments" className="font-semibold underline">Activer les paiements</Link></div>
        </div>
      )}

      <div className="grid overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 sm:grid-cols-2 lg:grid-cols-4" data-testid="compta-kpis">
        <Kpi i={0} icon={Wallet} label="Total encaissé" value={eur(data.total_received)} tint="#FF6A00" />
        <Kpi i={1} icon={Percent} label="Commission plateforme" value={eur(data.total_commission)} tint="#f87171" />
        <Kpi i={2} icon={TrendingUp} label="Net perçu" value={eur(data.net_received)} tint="#34d399" />
        <Kpi i={3} icon={Receipt} label="Paiements" value={data.count} tint="#FFD500" />
      </div>

      {data.connect_status === "active" && connectPk && (
        <StripeConnectProvider publishableKey={connectPk}>
          <div data-testid="compta-stripe" className="grid gap-6 lg:grid-cols-2">
            <Widget testid="compta-balance" icon={Wallet} title="Solde Stripe"><ConnectBalances /></Widget>
            <Widget testid="compta-payouts" icon={Landmark} title="Virements bancaires"><ConnectPayouts /></Widget>
          </div>
        </StripeConnectProvider>
      )}

      {data.fiscal && <FiscalBlock f={data.fiscal} periodLabel={data.period_label} />}

      <Widget testid="compta-chart" icon={BarChart3} title="Encaissements des 6 derniers mois">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.by_month} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
            <YAxis tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 12, color: "#fff" }} formatter={(v) => eur(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="gross" name="Encaissé" fill="#FF6A00" radius={[6, 6, 0, 0]} />
            <Bar dataKey="net" name="Net perçu" fill="#34d399" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Widget>

      <Widget testid="compta-tx" icon={List} title={`Paiements encaissés (${data.transactions.length})`} to="/app/devis" toLabel="Devis & Factures" bodyClass="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-zinc-800 text-xs uppercase text-zinc-500">
            <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Devis</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Montant</th><th className="px-4 py-3 text-right">Commission</th><th className="px-4 py-3 text-right">Net</th></tr>
          </thead>
          <tbody data-testid="compta-tx-list">
            {data.transactions.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">Aucun paiement encaissé pour le moment.</td></tr>}
            {data.transactions.map((t) => (
              <tr key={t.id} data-testid={`compta-tx-${t.id}`} className="border-b border-zinc-800/60 last:border-0">
                <td className="px-4 py-3 text-zinc-400">{frDate(t.date)}</td>
                <td className="px-4 py-3"><span className="font-medium text-white">{t.quote_number}</span> <span className="text-zinc-500">· {t.quote_title}</span></td>
                <td className="px-4 py-3 text-zinc-300">{t.client}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">{WHICH[t.which] || t.which}</span></td>
                <td className="px-4 py-3 text-right font-medium text-white">{eur(t.amount)}</td>
                <td className="px-4 py-3 text-right text-red-400">{t.commission ? `− ${eur(t.commission)}` : "—"}</td>
                <td className="px-4 py-3 text-right font-medium text-emerald-400">{eur(t.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Widget>
    </div>
  );
}
